import { ExpressBid, Route, TypeOptions } from "components/bid/form/partials";
import BaseBidForm from "components/shared/baseBidForm";
import ContactInfo from "components/shared/contactInfo";
import InlineBtn from "components/shared/inlineBtn";
import Loader from "components/shared/loader";
import { ProvideTabIndex } from "contexts/tabIndex";
import { comparePointsWithSegments } from "helpers/bid";
import { clearDates } from "helpers/bidpoint";
import { plainDate } from "helpers/date";
import {
    entityTypesEnum,
    featureModuleTypesEnum,
    mapObjectTypesEnum,
    organizationFlags,
    organizationFlagsEnum,
} from "helpers/enums";
import {
    formatExtendedProperties,
    formatExtendedPropertiesForSave,
    formatInitial,
    logInvalidKeys,
    serializeReq,
} from "helpers/form";
import { paths } from "helpers/paths";
import { flagVal } from "helpers/system";
import moment from "moment";
import policy from "policies";
import React from "react";
import { alert } from "services/alert";
import { getBidContact } from "sources/books/bids";
import { apply, getItem } from "sources/books/distributionBids";
import { checkDocuments } from "sources/books/organizationDocuments";
import { getDistributionBidItem as getRouteControlDistributionBidPoints } from "sources/books/routeControlPoints";
import { getRouteByDistributionBidId } from "sources/map";
import { showDefaultRouteOptions } from "sources/profile/organization";
import { Cargo, Expense, GroupElems, HiredCar, MainData, Point } from "./partials";

const autoSetMainContractFlagValue = flagVal(organizationFlags, organizationFlagsEnum.AutoSetMainContract);

export default class FormDistributionBid extends BaseBidForm {
    state = {
        isLoading: true,
        isValid: true,
        counterpartiesData: {},
    };

    constructor(props) {
        super(props);
        const { profile, roles, action, isPublic, config } = props;
        this.bidPolicy = policy("bid", { user: { profile, roles }, record: {}, options: { action, isPublic } });
        this.temperatureRegimeEnabled = profile.availableFeatureModules.includes(
            featureModuleTypesEnum.TemperatureRegime,
        );
        this.autoSetMainContractEnabled =
            (autoSetMainContractFlagValue & this.props.organization.intFlags) === autoSetMainContractFlagValue;
        if (!this.temperatureRegimeEnabled) {
            delete config.temperatureRegime;
        }
    }

    componentDidMount() {
        this.initForm();
        this.props.clearData();
        checkDocuments().then((res) => this.setState({ documentsExists: res }));
    }

    setValue = (changedData) => {
        const { name, value, option } = changedData;
        const isValid = this.isValidForm({ ...this.state, [name]: value });
        this.setState({ [name]: value, ...(isValid ? { isValid: true } : {}) });

        if (name === "counterpartyId") {
            const { legalPersonId } = this.state;
            this.updateCounterpartiesData(value, option);
            this.autoSelectCounterpartyContract(value, legalPersonId);
        }

        if (name === "contractId") {
            const { invoiceTriggerType, paymentPeriod, paymentPeriodType, paymentTypeId } = option || {};
            this.setState({ invoiceTriggerType, paymentPeriodInDays: paymentPeriod, paymentPeriodType, paymentTypeId });
        }
    };

    formatInitial = (bidRes) => {
        const { initialState, config, sourceId } = this.props;
        const bidData = formatInitial(bidRes, initialState, sourceId ? "edit" : this.props.action);
        bidData.isExpressBid ??= !!bidData.expressBid;
        bidData.expressBid ??= { settings: {} };

        const formattedBid = formatExtendedProperties(bidData, config.extendedProperties.config);
        const formattedCargos = bidData.cargos.map((c) =>
            formatExtendedProperties(c, config.cargos.config.extendedProperties.config),
        );
        const formattedPoints = bidData.bidPoints.map((p) =>
            formatExtendedProperties(p, config.bidPoints.config.extendedProperties.config),
        );
        if (this.props.isCopy) {
            clearDates(formattedPoints);
        }
        return {
            ...formattedBid,
            responsibleId: bidRes.responsible?.id || formattedBid.responsibleId,
            cargos: formattedCargos,
            bidPoints: formattedPoints,
            expenses: bidData.expenses ?? [],
        };
    };

    initForm = (nextProps) => {
        const { match, action, tmpData, initialState, editRoute, sourceId, loadNewItem } = nextProps || this.props;
        const defState = {
            isLoading: false,
            isValid: true,
            ...(this.temperatureRegimeEnabled
                ? { temperatureRegime: { temperatureMinimum: "", temperatureMaximum: "" } }
                : {}),
            ...initialState,
            ...tmpData,
        };

        if (action === "edit") {
            this.setState({ isLoading: true });
            return Promise.all([
                getItem(match.params.id),
                getRouteByDistributionBidId(match.params.id),
                showDefaultRouteOptions(),
            ]).then(([bidRes, routeRes, defaultRouteOptions]) => {
                this.checkCounterpartyPoints(bidRes);
                this.setState({
                    ...defState,
                    ...this.formatInitial(bidRes),
                    route: routeRes,
                    defaultRouteOptions,
                    routeIsLoaded: true,
                    editRoute,
                    pointsNotComparedToSegments: comparePointsWithSegments(
                        bidRes.bidPoints || this.state.bidPoints,
                        routeRes || this.state.route,
                        true,
                    ),
                });
                if (this.props.profile.availableFeatureModules.includes(featureModuleTypesEnum.RouteControlPoints)) {
                    getRouteControlDistributionBidPoints(bidRes.id).then((routeControlPoints) =>
                        this.setState({ routeControlPoints }),
                    );
                }
            });
        } else if (loadNewItem) {
            this.setState({ isLoading: true });
            return loadNewItem().then((bidRes) => {
                if (!bidRes) {
                    alert({ message: "Не удалось получить данные ЭЗЗ для создания заказа", type: "error" });
                    this.props.back();
                    return;
                }
                this.checkCounterpartyPoints(bidRes);
                this.setState({
                    ...defState,
                    ...this.formatInitial(bidRes),
                });
            });
        } else if (sourceId) {
            this.getDistributionBidBySource(sourceId, defState);
        }
        return this.setState(defState);
    };

    getDistributionBidBySource = (distributionBidId, defState) => {
        this.setState({ isLoading: true, routeIsLoaded: false });
        return getItem(distributionBidId).then((bidRes) => {
            this.checkCounterpartyPoints(bidRes);
            this.setState({
                ...defState,
                ...this.formatInitial({
                    ...bidRes,
                    id: undefined,
                    externalId: undefined,
                    cargos: bidRes.cargos.map((cargo) => ({ ...cargo, id: undefined })),
                    expenses: (bidRes.expenses ?? []).map((expense) => ({ ...expense, id: undefined })),
                    sourceDistributionBidId: parseInt(distributionBidId),
                }),
            });
        });
    };

    checkContactPersons = (state) => {
        const { contactPerson } = state;
        if (contactPerson) {
            if ((!contactPerson.phoneNumber || contactPerson.phoneNumber.length < 3) && !contactPerson.name) {
                state.contactPerson = null;
            }
        }

        const { bidPoints } = state;
        bidPoints.forEach((x) => {
            const { contactPerson } = x;
            if (contactPerson) {
                if ((!contactPerson.phoneNumber || contactPerson.phoneNumber.length < 3) && !contactPerson.name) {
                    x.contactPerson = null;
                }
            }
        });
    };

    saveBid = () => {
        const { isLoading, isValid, priceWithoutVatOnTop, isVatTop, hiredCarPriceWithoutVatOnTop, ...state } =
            this.state;
        if (isLoading) {
            return;
        }
        const validationState = { ...state, isVatTop, hiredCarPriceWithoutVatOnTop };
        if (this.isValidForm(validationState)) {
            this.setState({ isLoading: true, isValid: true });
            this.checkContactPersons(state);
            delete state.documentsExists;
            delete state.route;
            delete state.routeIsLoaded;
            if (!this.temperatureRegimeEnabled) {
                delete state.temperatureRegime;
            }

            const formatted = serializeReq({
                items: [
                    {
                        ...state,
                        isVatTop,
                        price: isVatTop && priceWithoutVatOnTop ? priceWithoutVatOnTop : state.price,
                        hiredCarPrice: state.isHiredCarVatTop ? hiredCarPriceWithoutVatOnTop : state.hiredCarPrice,
                        ...(this.state.hiredCarCompletedAt
                            ? { hiredCarCompletedAt: plainDate(this.state.hiredCarCompletedAt) }
                            : {}),
                    },
                ],
            });
            formatted.items[0].bidPoints.forEach((x) => {
                x.planEnterDate = moment(x.planEnterDate).format("YYYY-MM-DDTHH:mm");
                if (x.planLeaveDate) {
                    x.planLeaveDate = moment(x.planLeaveDate).format("YYYY-MM-DDTHH:mm");
                }
                if (x.secondaryPlanEnterDate) {
                    x.secondaryPlanEnterDate = moment(x.secondaryPlanEnterDate).format("YYYY-MM-DDTHH:mm");
                }
                if (!x.scenarioId) {
                    x.scenarioId = 0;
                }
            });

            const { config } = this.props;
            formatted.items[0].extendedProperties = formatExtendedPropertiesForSave(
                formatted.items[0],
                config.extendedProperties.config,
            );
            formatted.items[0].bidPoints.forEach((p) => {
                p.extendedProperties = formatExtendedPropertiesForSave(
                    p,
                    config.bidPoints.config.extendedProperties.config,
                );
                if (p.geozone.type === mapObjectTypesEnum.CounterpartyPoint) {
                    p.geozone.type = mapObjectTypesEnum.BidPoint;
                }
            });
            formatted.items[0].cargos.forEach((c) => {
                c.extendedProperties = formatExtendedPropertiesForSave(
                    c,
                    config.cargos.config.extendedProperties.config,
                );
            });
            if (formatted.items[0].clientBidDate) {
                formatted.items[0].clientBidDate = moment(formatted.items[0].clientBidDate).format("YYYY-MM-DD");
            }
            formatted.items[0].expenses?.forEach((expense) => {
                expense.value = Number(expense.value);
            });

            const submit = this.props.submit || apply;
            return submit(formatted).then((res) => (res || res === 0 ? this.successAction(res) : this.failureAction()));
        } else {
            alert({ message: "Сохранение не удалось из-за ошибки, заполните обязательные поля", type: "warning" });
            logInvalidKeys(this.props.config, validationState);
        }
        return Promise.resolve(this.setState({ isValid: false }));
    };

    successAction = (res) => {
        const result = Array.isArray(res) ? res[0] : res;
        const id = typeof result === "object" ? result?.id : result;
        if (id || id === 0) {
            this.props.history.push(paths.distributionBidShow(id));
            return;
        }
        this.failureAction();
    };

    onSubmit = (e) => {
        e && e.preventDefault();
        this.saveBid();
    };

    renderCargos = () => {
        const { cargos } = this.state;
        const { config, dictionaries, entityOptions, modal, roles, canEditBooks } = this.props;
        const props = {
            items: cargos,
            onChange: this.setValue,
            entity: "cargos",
            config: config.cargos.config,
            Element: Cargo,
            enums: this.props.enums,
            dictionaries,
            entityOptions,
            modal,
            roles,
            canEditBooks,
            partnerProps: {
                value: this.state.partnerId,
                onChange: this.setValue,
            },
        };
        return <GroupElems {...props} />;
    };

    renderExpenses = () => {
        const { expenses } = this.state;
        const { config, dictionaries, enums, modal } = this.props;
        const props = {
            items: expenses ?? [],
            onChange: this.setValue,
            entity: "expenses",
            config: config.expenses.config,
            Element: Expense,
            dictionaries,
            enums,
            modal,
        };
        return (
            <>
                <div className="form-group mt-3">
                    <strong>Расходы по заказу</strong>
                </div>
                <GroupElems {...props} />
                <div className="book-form__buttons">
                    <InlineBtn value="Добавить расход" cname="plus" action={this.addExpense} />
                </div>
            </>
        );
    };

    renderPoints = () => {
        const { bidPoints, counterpartiesData, counterpartyId } = this.state;
        const { switchMap, roles, enums, modal, profile, loadNewItem, organization } = this.props;
        const config = this.props.config.bidPoints.config;
        const props = {
            switchMap,
            roles,
            config,
            enums,
            modal,
            counterpartiesData,
            updateCounterpartiesData: this.updateCounterpartiesData,
            bidCounterpartyId: counterpartyId,
            availableFeatureModules: profile.availableFeatureModules,
            showUnresolvedAddressWarning: Boolean(loadNewItem),
            organization,
            profile,
        };

        return (
            <>
                <GroupElems
                    {...props}
                    items={bidPoints}
                    onChange={this.setValue}
                    entity="bidPoints"
                    Element={Point}
                    onPointRemoved={() => {
                        this.setState({
                            pointsNotComparedToSegments: comparePointsWithSegments(
                                this.state.bidPoints,
                                this.state.route,
                                true,
                            ),
                        });
                    }}
                />
                <div className="book-form__buttons">{this.renderPointActions()}</div>
            </>
        );
    };

    addExpense = () => {
        const { expenses } = this.state;
        this.setState({ expenses: [...(expenses ?? []), {}] });
    };

    renderActions = () => {
        const { action } = this.props;
        const value = action === "new" ? "Создать заказ" : "Обновить заказ";

        return <input type="submit" className="btn btn-brand mr-2 btn-sm" value={value} />;
    };

    renderHead = () => {
        return (
            <div className="book-form__head">
                <div className="book-form__title">
                    {this.props.title}
                    <a
                        className="book-form__head--btn btn btn-secondary float-right btn-sm d-none d-md-block d-lg-block"
                        onClick={() => this.setState({ editRoute: true })}
                    >
                        Изменить маршрут
                    </a>
                </div>
                <div className="book-form__close close close--sm" onClick={this.close} />
            </div>
        );
    };

    renderMainData = () => {
        const props = {
            bidPolicy: this.bidPolicy,
            renderField: this.renderField,
            renderSelect: this.renderSelect,
            renderCheckbox: this.renderCheckbox,
            renderDateField: this.renderDateField,
            config: this.props.config,
            profile: this.props.profile,
            modal: this.props.modal,
            roles: this.props.roles,
            organization: this.props.organization,
            priceWithoutVatOnTop: this.state.priceWithoutVatOnTop,
            price: this.state.price,
            isVatTop: this.state.isVatTop,
            documentsExists: this.state.documentsExists,
            documents: this.state.documents,
            setValue: this.setValue,
            temperatureRegimeEnabled: this.temperatureRegimeEnabled,
            temperatureRegime: this.state.temperatureRegime,
            counterpartyInsolvencyRegistryEntry:
                this.state.counterpartiesData[this.state.counterpartyId]?.insolvencyRegistryEntry,
            counterpartyComment: this.state.counterpartiesData[this.state.counterpartyId]?.comment,
            counterpartyId: this.state.counterpartyId,
            responsibleId: this.state.responsibleId,
            legalPersonId: this.state.legalPersonId,
            values: this.state,
            renderExpressBid: this.renderExpressBid,
            renderContactPerson: this.renderContactPerson,
            renderHiredCar: this.renderHiredCar,
            renderExtendedFields: this.renderExtendedFields,
        };
        return <MainData {...props} />;
    };

    renderExtendedFields = () => (
        <div className="d-flex flex-column">
            {this.renderExtendedProperties()}
            {this.renderTypeOptions()}
        </div>
    );

    renderContactPerson = () => {
        const props = {
            renderField: this.renderField,
            contact: this.state.contactPerson,
            setValue: this.setValue,
            config: this.props.config.contactPerson,
            getContactInfo: getBidContact,
            fieldName: "contactPerson",
        };
        return <ContactInfo {...props} />;
    };

    renderHiredCar = () => {
        return (
            <HiredCar
                config={this.props.config}
                hasHiredCarValue={this.state.hasHiredCar}
                renderCheckbox={this.renderCheckbox}
                renderField={this.renderField}
                renderDateField={this.renderDateField}
                renderSelect={this.renderSelect}
                onChange={this.setValue}
                hiredCarSettings={this.state}
                modal={this.props.modal}
                organization={this.props.organization}
                profile={this.props.profile}
                roles={this.props.roles}
            />
        );
    };

    renderTypeOptions = () => {
        const { entityOptions, enums, dictionaries, modal, roles, canEditBooks } = this.props;
        const props = {
            entityOptions,
            enums,
            dictionaries,
            setValue: this.setValue,
            entityType: entityTypesEnum.TruckingBid,
            typeOptionsValue: this.state.typeOptions || [],
            checkBidRelation: true,
            modal,
            roles,
            canEditBooks,
        };
        return <TypeOptions {...props} />;
    };

    renderExpressBid = () => {
        const { isExpressBid } = this.props.config;
        const expressBid = this.state.expressBid || {};
        const onChange = ({ name, value }) => {
            const expressBidValue = { ...this.state.expressBid, settings: { ...expressBid.settings, [name]: value } };
            this.setValue({ name: "expressBid", value: expressBidValue });
        };
        return (
            <ExpressBid
                config={{ isExpressBid, ...this.props.config["expressBid.settings"].config }}
                isExpressBidValue={this.state.isExpressBid}
                renderCheckbox={this.renderCheckbox}
                renderField={this.renderField}
                onChange={onChange}
                expressBidSettings={expressBid.settings || {}}
            />
        );
    };

    renderRoute = () => {
        if (this.props.action === "new") {
            return null;
        }
        if (!this.state.routeIsLoaded) {
            return <Loader />;
        }
        const {
            cargos,
            bidPoints,
            route,
            defaultRouteOptions,
            id,
            editRoute,
            confirmRoute,
            carOption,
            refueling,
            pointsNotComparedToSegments,
            routeControlPoints,
        } = this.state;
        const { modal, tileUrls, history, profile, roles, canEditBooks } = this.props;
        const getBidInfo = (id) => {
            return Promise.all([getItem(id), getRouteByDistributionBidId(id)]).then(([bidRes, routeRes]) => {
                this.setState({
                    ...this.formatInitial(bidRes),
                    route: routeRes,
                    routeIsLoaded: true,
                    isLoading: false,
                });
                return [bidRes, routeRes];
            });
        };
        const props = {
            pointsNotComparedToSegments,
            cargos,
            refueling,
            bidPoints,
            route,
            defaultRouteOptions,
            modal,
            tileUrls,
            getBidInfo,
            history,
            submitBid: () => this.saveBid({ stayOpened: true }),
            setValue: this.setValue,
            onCloseAction: this.reloadRoute,
            availableFeatureModules: profile.availableFeatureModules,
            bidId: id,
            carOption,
            canEditBooks,
            editRoute,
            confirmRoute,
            fromShowPage: this.props.confirmRoute,
            kind: "distributionBid",
            roles,
            routeControlPoints,
        };
        return <Route {...props} />;
    };

    reloadRoute = () => {
        const { match } = this.props;
        const { id } = this.state;
        this.setState({ routeIsLoaded: false });
        getRouteByDistributionBidId(match.params.id || id).then((route) =>
            this.setState({ route, routeIsLoaded: true }),
        );
    };

    renderBody = () => {
        const { statusIsLoading } = this.state;
        if (statusIsLoading) {
            return <Loader />;
        }
        return (
            <>
                <ProvideTabIndex value={{ baseTabIndex: 100 }}>{this.renderMainData()}</ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 200 }}>{this.renderCargos()}</ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 300 }}>{this.renderExpenses()}</ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 400 }}>{this.renderPoints()}</ProvideTabIndex>
                {this.renderRoute()}
            </>
        );
    };
}
